import {useState} from "react";

export type SearchableGroupFilter = {
  key: string
  columns: string[]
  searchOperator: SearchOperator
  values: string[]
}
export type SearchOperator = 'IN' | 'LESSER_OR_EQUAL' | 'GREATER_OR_EQUAL' | 'ILIKE' | 'NOT_IN'
export type SearchableFilter = {
  column: string
  searchOperator: SearchOperator
  values: string[]
}
export type SearchableFilterGroup = {
  filters: SearchableFilter[]
  filterGroupType: string
}

export type SearchableOrder = {
  orderBy: string
  orderValues: string[]
  orderDirection: 'ASCENDING' | 'DESCENDING' | 'NORMAL'
}

export type Searchable = {
  fullTextSearch: string | null
  filters: SearchableFilter[]
  groupedFilters: SearchableGroupFilter[]
  filtersGroups: SearchableFilterGroup[]
  ordering: SearchableOrder[]
  page: number
  pageSize: number
  availableValues: string[]
}

export const copyWithOrdering = (searchable: Searchable, pageSize: number): Searchable => ({
  fullTextSearch: null,
  filters: [],
  groupedFilters: [],
  filtersGroups: [],
  ordering: searchable.ordering,
  page: 1,
  pageSize: pageSize,
  availableValues: []
})

export type SearchableState = {
  clearFilters: () => void
  searchable: Searchable,
  fullTextSearch: string | null
  onFullTextSearch: (value: string) => void
  onFilters: (filter: SearchableFilter | SearchableFilter[]) => void
  getFilterValues: (column: string, searchOperator: SearchOperator) => string[]
  onGroupedFilters: (key: string, searchOperator: SearchOperator, columns: string[], values: string[]) => void
  getGroupedFilterValues: (key: string, searchOperator: SearchOperator) => string[]
  onOrdering: (ordering: SearchableOrder) => void
  onPage: (value: number) => void
  onPageSize: (value: number) => void
  extractAvailableValues: (value: any, column: string) => string[]
  setAvailableValues: (values: string[]) => void
}

export const useSearchableState = (initialAvailableValues: string[], fullTextSearchColumns: string[], initialFilter?: SearchableFilter): SearchableState =>
  useSearchableStateWithDefaultValues(initialAvailableValues, fullTextSearchColumns, [], initialFilter)

export const useSearchableStateWithOrdering = (initialAvailableValues: string[], fullTextSearchColumns: string[], initialOrdering: SearchableOrder[], initialFilter?: SearchableFilter, initialAndFilters?: SearchableFilter[]): SearchableState =>
  useSearchableStateWithDefaultValues(initialAvailableValues, fullTextSearchColumns, initialOrdering, initialFilter, initialAndFilters)

const useSearchableStateWithDefaultValues = (initialAvailableValues: string[], fullTextSearchColumns: string[], initialOrdering: SearchableOrder[], initialFilter?: SearchableFilter, initialAndFilters?: SearchableFilter[]): SearchableState => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [ordering, setOrdering] = useState<SearchableOrder[]>(initialOrdering)

  const [fullTextSearch, setFullTextSearch] = useState<string | null>(null)

  const [andFilters, setAndFilters] = useState<SearchableFilter[]>(initialAndFilters ?? [])
  const [orFilters, setOrFilters] = useState<SearchableFilter[]>(initialFilter ? [initialFilter] : [])
  const [groupedFilters, setGroupedFilters] = useState<SearchableGroupFilter[]>([])

  const [availableValues, setAvailableValues] = useState(initialAvailableValues)


  const onFilters = (filters: SearchableFilter | SearchableFilter[]) => {
    // Znormalizuj do tablicy
    const filtersArray = Array.isArray(filters) ? filters : [filters];

    setAndFilters(prev => {
      // Kopia aktualnych filtrów
      let updated = [...prev];

      filtersArray.forEach(filter => {
        if (filter.values.length === 0) {
          // Usuń filtr, jeśli brak wartości
          updated = updated.filter(it => !(it.column === filter.column && it.searchOperator === filter.searchOperator));
        } else {
          const existingIndex = updated.findIndex(it => it.column === filter.column && it.searchOperator === filter.searchOperator);
          if (existingIndex !== -1) {
            // Aktualizuj istniejący filtr
            updated[existingIndex] = { ...updated[existingIndex], values: filter.values, searchOperator: filter.searchOperator };
          } else {
            // Dodaj nowy filtr
            updated.push(filter);
          }
        }
      });

      return updated;
    });
  };

  return {
    getGroupedFilterValues: (key, searchOperator) => {
      return groupedFilters.find(it => it.key === key && it.searchOperator === searchOperator)?.values ?? [];
    },
    onGroupedFilters: (key, searchOperator, columns, values) => {
      if(values.length === 0) {
        setGroupedFilters(groupedFilters.filter(it => it.key !== key))
      } else {
        const existingFilter = groupedFilters.find(it => it.key === key && it.searchOperator === searchOperator)
        if(existingFilter) {
          const updatedGroupedFilters = groupedFilters.map(it => {
            if(it.key === key && it.searchOperator === searchOperator) {
              it.values = values
              it.searchOperator = searchOperator
              it.columns = columns
            }
            return it
          })
          setGroupedFilters(updatedGroupedFilters)
        } else {
          console.log('Test - 3')
          setGroupedFilters([...groupedFilters, {
            key: key,
            columns: columns,
            searchOperator: searchOperator,
            values: values
          }])
        }
      }
    },
    fullTextSearch: fullTextSearch,
    onFullTextSearch: (value) => {
      setFullTextSearch(value === '' ? null : value)
      if(value === '' || value === null || value === undefined) {
        setOrFilters([])
      } else {
        let fullTextFilters: SearchableFilter[] = fullTextSearchColumns.map(it => ({
          column: it,
          searchOperator: 'ILIKE',
          values: [`%${value}%`]
        }))
        setOrFilters(fullTextFilters)
      }
    },
    searchable: {
      fullTextSearch: fullTextSearch,
      filters: andFilters,
      groupedFilters: groupedFilters,
      filtersGroups: [
        (andFilters.length > 0 ? {filterGroupType: 'AND', filters: andFilters} : null),
        (orFilters.length > 0 ? {filterGroupType: 'OR', filters: orFilters} : null),
        ...(groupedFilters?.length > 0 ? groupedFilters.map(filter => ({
          filterGroupType: 'OR',
          filters: filter.columns.map(it => ({
            column: it,
            searchOperator: filter.searchOperator,
            values: filter.values
          }))
        })) : [])
      ].filter(it => it != null),
      ordering: ordering,
      page: page,
      pageSize: pageSize,
      availableValues: availableValues,
    },
    clearFilters: () => {
      setAndFilters([])
    },
    setAvailableValues: setAvailableValues,
    extractAvailableValues: (available: any, column: string) => {
      return ((available[column] ?? []) as string[])
    },
    onFilters: onFilters,
    getFilterValues: (column, searchOperator) => andFilters.find(it => it.column === column && it.searchOperator === searchOperator)?.values ?? [],
    onPage: setPage,
    onPageSize: setPageSize,
    onOrdering: (value) => {
      const exists = ordering.find(it => it.orderBy === value.orderBy)
      if(exists === null || exists === undefined) {
        setOrdering([...ordering, value])
      } else {
        if(value.orderDirection === 'NORMAL') {
          const updatedOrdering = ordering
            .filter(it => it.orderBy !== value.orderBy)
          setOrdering(updatedOrdering)
        } else {
          const updatedOrdering = ordering
            .map(it => {
              if(it.orderBy === value.orderBy) {
                it.orderDirection = value.orderDirection
              }
              return it;
            })
          setOrdering(updatedOrdering)
        }
      }
    }
  }
}
